// Comprehensive test to verify fix effectiveness
import html2canvas from 'html2canvas';

export const comprehensiveAlignmentTest = async (canvasId) => {
  console.log('🧪 COMPREHENSIVE ALIGNMENT TEST - Starting...');
  
  const canvasElement = document.getElementById(canvasId);
  if (!canvasElement) {
    console.error('Canvas element not found');
    return { success: false, message: 'Canvas not found' };
  }

  // Test 1: Check computed styles in live preview
  console.log('🔍 Test 1: Checking live preview computed styles...');
  const pills = canvasElement.querySelectorAll('.chip'); // Changed from .pill to .chip
  const ctaButton = canvasElement.querySelector('.cta');
  const heading = canvasElement.querySelector('.main-heading');

  const previewStyles = {
    pills: Array.from(pills).map((pill, i) => {
      const styles = window.getComputedStyle(pill);
      return {
        index: i,
        text: pill.textContent,
        height: styles.height,
        lineHeight: styles.lineHeight,
        padding: styles.padding,
        fontSize: styles.fontSize,
        display: styles.display
      };
    }),
    cta: ctaButton ? {
      text: ctaButton.textContent,
      height: window.getComputedStyle(ctaButton).height,
      lineHeight: window.getComputedStyle(ctaButton).lineHeight,
      padding: window.getComputedStyle(ctaButton).padding,
      fontSize: window.getComputedStyle(ctaButton).fontSize,
      display: window.getComputedStyle(ctaButton).display
    } : null,
    heading: heading ? {
      fontSize: window.getComputedStyle(heading).fontSize
    } : null
  };
  
  console.log('📊 Preview Styles:', previewStyles);

  // Test 2: Take screenshot for comparison
  console.log('📸 Test 2: Capturing screenshots...');
  
  // Ensure fonts are loaded
  if (document.fonts && document.fonts.ready) {
    await document.fonts.ready;
  }
  await new Promise(resolve => setTimeout(resolve, 500));

  // Low-scale preview (1x)
  const previewCanvas = await html2canvas(canvasElement, {
    width: 400,
    height: 400,
    scale: 1,
    useCORS: true,
    allowTaint: true,
    backgroundColor: null,
    logging: false
  });

  // Production-scale export (2x)
  const exportCanvas = await html2canvas(canvasElement, {
    width: 400,
    height: 400,
    scale: 2,
    useCORS: true,
    allowTaint: true,
    backgroundColor: null,
    fontLoading: true,
    letterRendering: true,
    logging: false,
    onclone: (clonedDoc) => {
      // Our simplified onclone - just ensure fonts
      const existingFontLink = clonedDoc.querySelector('link[href*="fonts.googleapis.com"]');
      if (!existingFontLink) {
        const fontLink = clonedDoc.createElement('link');
        fontLink.href = 'https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700;800&display=swap';
        fontLink.rel = 'stylesheet';
        clonedDoc.head.appendChild(fontLink);
      }

      // Debug: Log cloned styles
      const clonedPills = clonedDoc.querySelectorAll('.chip'); // Changed from .pill to .chip
      const clonedCta = clonedDoc.querySelector('.cta'); // Changed from .cta-button to .cta
      console.log('🔍 Cloned DOM check:');
      console.log('Pills found:', clonedPills.length);
      console.log('CTA found:', !!clonedCta);
      
      clonedPills.forEach((pill, i) => {
        console.log(`Pill ${i} classes:`, pill.className);
        console.log(`Pill ${i} inline style:`, pill.style.cssText);
      });
      
      if (clonedCta) {
        console.log('CTA classes:', clonedCta.className);
        console.log('CTA inline style:', clonedCta.style.cssText);
      }
    }
  });

  // Test 3: Create pixel comparison
  console.log('📏 Test 3: Analyzing results...');
  
  const previewURL = previewCanvas.toDataURL('image/png');
  const exportURL = exportCanvas.toDataURL('image/png');
  
  // Save both images
  const timestamp = Date.now();
  
  const previewLink = document.createElement('a');
  previewLink.download = `preview-test-${timestamp}.png`;
  previewLink.href = previewURL;
  previewLink.click();
  
  await new Promise(resolve => setTimeout(resolve, 1000));
  
  const exportLink = document.createElement('a');
  exportLink.download = `export-test-${timestamp}.png`;
  exportLink.href = exportURL;
  exportLink.click();

  // Test 4: Verify the key fixes
  const testResults = {
    pillsUsingPadding: previewStyles.pills.every(pill => 
      !pill.height.includes('32px') || !pill.lineHeight.includes('32px')
    ),
    ctaUsingPadding: previewStyles.cta ? 
      (!previewStyles.cta.height.includes('40px') || !previewStyles.cta.lineHeight.includes('40px')) : 
      true,
    headingSizeWorking: previewStyles.heading ? 
      parseInt(previewStyles.heading.fontSize) !== 16 : // Default would be 16px
      true
  };

  console.log('✅ Test Results:');
  console.log('- Pills using padding-based centering:', testResults.pillsUsingPadding);
  console.log('- CTA using padding-based centering:', testResults.ctaUsingPadding);
  console.log('- Heading size control working:', testResults.headingSizeWorking);
  
  const overallSuccess = testResults.pillsUsingPadding && 
                         testResults.ctaUsingPadding && 
                         testResults.headingSizeWorking;
  
  console.log('🏁 Overall Test Result:', overallSuccess ? 'PASS ✅' : 'FAIL ❌');
  
  return {
    success: overallSuccess,
    results: testResults,
    previewStyles,
    message: overallSuccess ? 
      'All alignment fixes working correctly!' : 
      'Some issues remain - check console for details'
  };
};
